import React from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList
} from 'recharts';

/**
 * Bug Cycle Time Chart Component
 * Displays a stacked column chart showing cycle time metrics by month
 */
const BugCycleTimeChart = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No cycle time data available. Make sure bugs are linked to TSI tickets.
      </Alert>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3, width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Bug Lifecycle Cycle Times by Month
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Average cycle times for customer reported issues (Investigation tickets linked to bugs)
      </Typography>
      
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="month" 
            angle={-45}
            textAnchor="end"
            height={80}
            tick={{ fontSize: 12 }}
          />
          <YAxis 
            label={{ value: 'Average Days', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip 
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                return (
                  <Box sx={{ 
                    backgroundColor: 'white', 
                    padding: '10px', 
                    border: '1px solid #ccc',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {label}
                    </Typography>
                    {payload.map((entry, index) => (
                      <Typography key={index} variant="body2" sx={{ color: entry.color, mb: 0.5 }}>
                        {entry.name}: {entry.value?.toFixed(1)} days
                      </Typography>
                    ))}
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
                      Total Cycle Time: {total.toFixed(1)} days
                    </Typography>
                  </Box>
                );
              }
              return null;
            }}
          />
          <Legend 
            verticalAlign="top" 
            height={36}
            wrapperStyle={{ paddingBottom: '20px' }}
          />
          
          {/* Stacked bars for cycle time metrics */}
          <Bar 
            dataKey="tsiCreatedToActivated" 
            name="TSI Created to Activated"
            stackId="cycleTime"
            fill="#8884d8"
          >
            <LabelList 
              dataKey="tsiCreatedToActivated" 
              position="inside" 
              formatter={(value) => value > 0 ? value.toFixed(1) : ''}
              style={{ fill: '#fff', fontSize: '11px', fontWeight: 'bold' }}
            />
          </Bar>
          <Bar 
            dataKey="tsiActivatedToBugCreated" 
            name="TSI Activated to Bug Created"
            stackId="cycleTime"
            fill="#82ca9d"
          >
            <LabelList 
              dataKey="tsiActivatedToBugCreated" 
              position="inside" 
              formatter={(value) => value > 0 ? value.toFixed(1) : ''}
              style={{ fill: '#fff', fontSize: '11px', fontWeight: 'bold' }}
            />
          </Bar>
          <Bar 
            dataKey="bugCreatedToBugClosed" 
            name="Bug Created to Bug Closed"
            stackId="cycleTime"
            fill="#ffc658"
          >
            <LabelList 
              dataKey="bugCreatedToBugClosed" 
              position="top" 
              formatter={(value) => value > 0 ? value.toFixed(1) : ''}
              style={{ fill: '#333', fontSize: '11px', fontWeight: 'bold' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="textSecondary">
          <strong>TSI Created to Activated:</strong> Average time between Created Date and Activated Date for TSI tickets linked to bugs
        </Typography>
        <br />
        <Typography variant="caption" color="textSecondary">
          <strong>TSI Activated to Bug Created:</strong> Average time between Activated Date on TSI and Created Date of Bug
        </Typography>
        <br />
        <Typography variant="caption" color="textSecondary">
          <strong>Bug Created to Bug Closed:</strong> Average time between Creation of Bug to Closure of Bug
        </Typography>
      </Box>
    </Paper>
  );
};

export default BugCycleTimeChart;

